/**
 * Unified Date Utilities
 * 
 * Centralized date handling with consistent formatting and validation
 * Replaces fragmented date utilities across the codebase
 * 
 * Usage:
 *   import { dateUtils } from '@/utils/dateUtils';
 *   const formatted = dateUtils.formatForDisplay(new Date());
 */

import { logger } from './logger';

// ================ CONSTANTS ================

const MIN_HOURS_AHEAD = 48; // Campaigns must start 48 hours from now

// ================ DATE PARSING ================

/**
 * Safely parse a date from various input formats
 * Returns null if parsing fails
 */
function parseDate(input: string | Date | null | undefined): Date | null {
  if (!input) {
    return null;
  }

  try {
    // Already a Date object
    if (input instanceof Date) {
      return isNaN(input.getTime()) ? null : input;
    }

    // Try ISO string or standard formats
    let date = new Date(input);
    if (!isNaN(date.getTime())) {
      return date;
    }

    // Try YYYY-MM-DD format explicitly
    if (/^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      const [year, month, day] = input.trim().split('-').map(Number);
      date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Try DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(input.trim())) {
      const parts = input.trim().split('/').map(Number);
      // Try both DD/MM/YYYY and MM/DD/YYYY
      date = new Date(parts[2], parts[1] - 1, parts[0]);
      if (!isNaN(date.getTime())) {
        return date;
      }
      date = new Date(parts[2], parts[0] - 1, parts[1]);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    logger.warn('Failed to parse date', { input });
    return null;
  } catch (error) {
    logger.error('Error parsing date', error, { input });
    return null;
  }
}

// ================ DATE FORMATTING ================

/**
 * Format date for display (e.g., "January 15, 2024")
 */
function formatForDisplay(input: string | Date | null): string {
  const date = parseDate(input);
  if (!date) return 'Not specified';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format date as ISO string (YYYY-MM-DD)
 */
function formatToISO(input: string | Date | null): string | null {
  const date = parseDate(input);
  if (!date) return null;

  return date.toISOString().split('T')[0];
}

/**
 * Format date for storage/database
 */
function formatForStorage(input: string | Date | null): string | null {
  return formatToISO(input);
}

/**
 * Format date with time
 */
function formatWithTime(input: string | Date | null): string {
  const date = parseDate(input);
  if (!date) return 'Not specified';

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ================ DATE VALIDATION ================

/**
 * Check if a date is valid
 */
function isValid(input: string | Date | null): boolean {
  const date = parseDate(input);
  return date !== null && !isNaN(date.getTime());
}

/**
 * Get minimum allowed campaign start date (48 hours from now)
 */
function getMinStartDate(): Date {
  const minDate = new Date();
  minDate.setHours(minDate.getHours() + MIN_HOURS_AHEAD);
  return minDate;
}

/**
 * Validate that a start date meets the 48-hour minimum requirement
 */
function validateStartDate(input: string | Date | null): { valid: boolean; error?: string } {
  const date = parseDate(input);
  
  if (!date) {
    return { valid: false, error: 'Invalid date' };
  }

  const minDate = getMinStartDate();
  
  if (date < minDate) {
    return {
      valid: false,
      error: `Campaign must start at least ${MIN_HOURS_AHEAD} hours from now`
    };
  }

  return { valid: true };
}

/**
 * Validate that end date is after start date
 */
function validateDateRange(
  startInput: string | Date | null,
  endInput: string | Date | null
): { valid: boolean; error?: string } {
  const startDate = parseDate(startInput);
  const endDate = parseDate(endInput);

  if (!startDate) {
    return { valid: false, error: 'Invalid start date' };
  }

  if (!endDate) {
    return { valid: false, error: 'Invalid end date' };
  }

  if (endDate <= startDate) {
    return { valid: false, error: 'End date must be after start date' };
  }

  return { valid: true };
}

// ================ DATE CALCULATIONS ================

/**
 * Calculate duration in days between two dates (inclusive)
 */
function calculateDuration(
  startInput: string | Date | null,
  endInput: string | Date | null
): number | null {
  const startDate = parseDate(startInput);
  const endDate = parseDate(endInput);

  if (!startDate || !endDate) {
    return null;
  }

  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
}

/**
 * Add days to a date
 */
function addDays(input: string | Date | null, days: number): Date | null {
  const date = parseDate(input);
  if (!date) return null;

  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if a date is in the past
 */
function isPast(input: string | Date | null): boolean {
  const date = parseDate(input);
  if (!date) return false;

  return date < new Date();
}

/**
 * Check if a date is in the future
 */
function isFuture(input: string | Date | null): boolean {
  const date = parseDate(input);
  if (!date) return false;

  return date > new Date();
}

// ================ EXPORT ================

export const dateUtils = {
  // Parsing
  parse: parseDate,
  
  // Formatting
  formatForDisplay,
  formatToISO,
  formatForStorage,
  formatWithTime,
  
  // Validation
  isValid,
  validateStartDate,
  validateDateRange,
  getMinStartDate,
  
  // Calculations
  calculateDuration,
  addDays,
  isPast,
  isFuture,
  
  // Constants
  MIN_HOURS_AHEAD
};

// Named exports for convenience
export {
  parseDate,
  formatForDisplay,
  formatToISO,
  formatForStorage,
  formatWithTime,
  isValid as isValidDate,
  validateStartDate,
  validateDateRange,
  getMinStartDate,
  calculateDuration,
  addDays,
  isPast,
  isFuture
};
